/**
 * BudgetVsActual — sección completa del Finance Hub.
 *
 * Muestra:
 *   - Tabla global por villa (status agregado, barra, click para expandir)
 *   - Al expandir una villa, desglose por categoría
 *   - Cada categoría `unbudgeted` con gasto > 0 muestra CTA "Definir presupuesto"
 *
 * Principios de UX:
 *   - `unbudgeted` NUNCA se pinta como 0%: línea discontinua, texto gris.
 *   - `ok con 0% gastado` es normal y sano: barra verde vacía visible.
 *   - Villas sin actividad y sin presupuesto están ocultas por defecto.
 */
import React, { useEffect, useMemo, useState } from "react";
import { api, formatError } from "@/lib/api";
import { Section } from "@/pages/Shell";
import { BudgetBar, BudgetStatusChip } from "@/components/admin/BudgetBar";
import { CaretRight, CaretDown, Plus } from "@phosphor-icons/react";

const CATEGORY_LABELS = {
  materials: "Materiales",
  labor: "Mano de obra",
  permits: "Permisos",
  utilities: "Suministros",
  services: "Servicios",
  logistics: "Logística",
  furnishing: "Mobiliario / FF&E",
  other: "Otros",
};

const ALL_CATEGORIES = ["materials", "labor", "permits", "utilities", "services", "logistics", "furnishing", "other"];

export default function BudgetVsActual() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [expanded, setExpanded] = useState(null);   // villa_id expanded
  const [showAll, setShowAll] = useState(false);
  const [defineFor, setDefineFor] = useState(null); // { villa_id, villa_name, category? }

  const load = async () => {
    setLoading(true); setErr("");
    try {
      const r = await api.get("/budgets/summary/global");
      setRows(r.data || []);
    } catch (e) { setErr(formatError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const visibleRows = useMemo(() => {
    if (showAll) return rows;
    // Default: hide villas without activity AND without budget (reduces noise)
    return rows.filter(r => (r.total_actual_eur || 0) > 0 || (r.total_budget_eur || 0) > 0);
  }, [rows, showAll]);

  const hiddenCount = rows.length - visibleRows.length;

  return (
    <Section
      title="Plan vs Gasto"
      subtitle="Presupuesto previsto y gasto real por villa. Click en una villa para ver el desglose por categoría."
    >
      {loading && (
        <div className="dv-card text-sm text-stone-500 text-center py-4" data-testid="budget-loading">
          Cargando presupuestos…
        </div>
      )}
      {err && (
        <div className="dv-card text-sm text-red-700" data-testid="budget-error">{err}</div>
      )}

      {!loading && !err && (
        <>
          <div className="dv-card divide-y divide-stone-100" data-testid="budget-table">
            {visibleRows.map((row) => (
              <BudgetVillaRow
                key={row.villa_id}
                row={row}
                expanded={expanded === row.villa_id}
                onToggle={() => setExpanded(expanded === row.villa_id ? null : row.villa_id)}
                onDefine={(cat) => setDefineFor({
                  villa_id: row.villa_id,
                  villa_name: row.villa_name,
                  category: cat,
                })}
                onChanged={load}
              />
            ))}
            {visibleRows.length === 0 && (
              <div className="py-6 text-center text-sm text-stone-500">
                No hay villas con presupuesto ni actividad financiera todavía.
              </div>
            )}
          </div>

          {hiddenCount > 0 && (
            <button
              onClick={() => setShowAll(!showAll)}
              className="mt-3 text-xs text-stone-500 hover:text-stone-900 underline"
              data-testid="budget-toggle-hidden"
            >
              {showAll
                ? "Ocultar villas sin actividad"
                : `Mostrar ${hiddenCount} villa${hiddenCount === 1 ? "" : "s"} sin actividad`}
            </button>
          )}
        </>
      )}

      {defineFor && (
        <DefineBudgetModal
          ctx={defineFor}
          onClose={() => setDefineFor(null)}
          onSaved={async () => { setDefineFor(null); await load(); }}
        />
      )}
    </Section>
  );
}

// ---------- Row (villa-level) ----------
function BudgetVillaRow({ row, expanded, onToggle, onDefine, onChanged }) {
  const { villa_id, villa_name, total_budget_eur, total_actual_eur, pct, status } = row;
  return (
    <div data-testid={`budget-row-${villa_id}`}>
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-3 py-3 px-1 hover:bg-stone-50 transition-colors text-left"
        data-testid={`budget-row-toggle-${villa_id}`}
      >
        <div className="w-4 shrink-0 text-stone-400">
          {expanded ? <CaretDown size={14} /> : <CaretRight size={14} />}
        </div>
        <div className="w-[220px] shrink-0">
          <div className="text-sm font-medium">{villa_name}</div>
          <div className="mt-1"><BudgetStatusChip status={status} testId={`budget-chip-${villa_id}`} /></div>
        </div>
        <div className="flex-1 min-w-0">
          <BudgetBar
            budget={total_budget_eur}
            actual={total_actual_eur}
            pct={pct}
            status={status}
            testId={`budget-bar-${villa_id}`}
          />
        </div>
      </button>

      {expanded && (
        <VillaBreakdown
          villa_id={villa_id}
          villa_name={villa_name}
          onDefine={onDefine}
          onChanged={onChanged}
        />
      )}
    </div>
  );
}

// ---------- Per-category breakdown ----------
function VillaBreakdown({ villa_id, villa_name, onDefine, onChanged }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [busyCat, setBusyCat] = useState(null);

  useEffect(() => {
    let cancel = false;
    api.get(`/budgets/summary/villa/${villa_id}`)
      .then(r => { if (!cancel) setData(r.data); })
      .catch(e => { if (!cancel) setErr(formatError(e)); });
    return () => { cancel = true; };
  }, [villa_id]);

  const handleDeleteBudget = async (category) => {
    const ok = window.confirm(
      `¿Eliminar el presupuesto de "${category}" para ${villa_name}?\n\n` +
      "Las facturas asociadas no se borran. Sólo desaparece la línea de presupuesto."
    );
    if (!ok) return;
    setBusyCat(category);
    try {
      // Resolve the budget_id from the budgets list
      const r = await api.get("/budgets", { params: { villa_id, category } });
      const budget = (r.data || [])[0];
      if (!budget) throw new Error("No se encontró el presupuesto");
      await api.delete(`/budgets/${budget.budget_id}`);
      // Refresh local breakdown
      const r2 = await api.get(`/budgets/summary/villa/${villa_id}`);
      setData(r2.data);
      onChanged?.();
    } catch (e) {
      alert(formatError(e));
    } finally { setBusyCat(null); }
  };

  if (err) return <div className="pl-8 pb-3 text-xs text-red-700">{err}</div>;
  if (!data) return <div className="pl-8 pb-3 text-xs text-stone-400">Cargando desglose…</div>;

  const lines = data.lines || [];
  const presentCats = new Set(lines.map(l => l.category));
  const missingCats = ALL_CATEGORIES.filter(c => !presentCats.has(c));

  return (
    <div className="pl-8 pr-2 pb-3 space-y-2" data-testid={`budget-breakdown-${villa_id}`}>
      {lines.map((line) => (
        <div
          key={line.category}
          className="flex items-center gap-3 py-1.5 text-sm"
          data-testid={`budget-line-${villa_id}-${line.category}`}
        >
          <div className="w-[180px] shrink-0">
            <div className="text-sm text-stone-700">{CATEGORY_LABELS[line.category] || line.category}</div>
            <div className="mt-0.5">
              <BudgetStatusChip status={line.status} testId={`budget-line-chip-${villa_id}-${line.category}`} />
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <BudgetBar
              budget={line.budget_eur}
              actual={line.actual_eur}
              pct={line.pct}
              status={line.status}
              testId={`budget-line-bar-${villa_id}-${line.category}`}
            />
          </div>
          {line.status === "unbudgeted" ? (
            <button
              onClick={() => onDefine(line.category)}
              className="text-xs text-[#9C5236] hover:underline whitespace-nowrap shrink-0"
              data-testid={`budget-define-inline-${villa_id}-${line.category}`}
            >
              Definir presupuesto
            </button>
          ) : (
            <button
              onClick={() => handleDeleteBudget(line.category)}
              disabled={busyCat === line.category}
              title="Eliminar presupuesto de esta categoría"
              data-testid={`budget-delete-${villa_id}-${line.category}`}
              className="text-xs text-red-700 hover:bg-red-50 px-2 py-0.5 rounded border border-red-200 whitespace-nowrap shrink-0 disabled:opacity-50"
            >
              {busyCat === line.category ? "…" : "Eliminar"}
            </button>
          )}
        </div>
      ))}

      {missingCats.length > 0 && (
        <div className="pt-2 mt-1 border-t border-stone-100">
          <button
            onClick={() => onDefine(null)}
            className="inline-flex items-center gap-1 text-xs text-stone-600 hover:text-stone-900"
            data-testid={`budget-define-new-${villa_id}`}
          >
            <Plus size={12} weight="bold" /> Añadir presupuesto en otra categoría
          </button>
        </div>
      )}
    </div>
  );
}

// ---------- Modal: definir presupuesto ----------
function DefineBudgetModal({ ctx, onClose, onSaved }) {
  const [category, setCategory] = useState(ctx.category || "materials");
  const [amount, setAmount] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    const amt = Number(amount);
    if (!amt || amt <= 0) { setErr("El importe debe ser mayor que 0."); setBusy(false); return; }
    if (amt > 10_000_000) { setErr("El importe parece excesivo (>10M€). Revísalo."); setBusy(false); return; }
    try {
      await api.post("/budgets", {
        villa_id: ctx.villa_id,
        category,
        amount_eur: amt,
        notes: notes || null,
      });
      await onSaved();
    } catch (e) { setErr(formatError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="define-budget-modal">
      <div className="bg-white rounded-lg max-w-md w-full p-6">
        <div className="text-xs tracking-[0.3em] uppercase text-stone-500 mb-1">Presupuesto</div>
        <div className="font-serif-dv text-2xl mb-1">{ctx.villa_name}</div>
        <div className="text-stone-500 text-sm mb-4">Fija el presupuesto previsto para una categoría.</div>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="text-xs text-stone-500">Categoría</label>
            <select
              className="dv-input"
              value={category}
              onChange={e => setCategory(e.target.value)}
              disabled={!!ctx.category}
              data-testid="define-budget-category"
            >
              {ALL_CATEGORIES.map(c => (
                <option key={c} value={c}>{CATEGORY_LABELS[c]}</option>
              ))}
            </select>
            {ctx.category && (
              <div className="text-xs text-stone-400 mt-1">Categoría fijada desde la tabla.</div>
            )}
          </div>
          <div>
            <label className="text-xs text-stone-500">Importe previsto (EUR)</label>
            <input
              type="number"
              inputMode="numeric"
              min="1" max="10000000"
              required
              className="dv-input"
              value={amount}
              onChange={e => setAmount(e.target.value)}
              placeholder="Ej: 15000"
              data-testid="define-budget-amount"
            />
          </div>
          <div>
            <label className="text-xs text-stone-500">Notas (opcional)</label>
            <textarea
              rows={2}
              className="dv-input"
              value={notes}
              onChange={e => setNotes(e.target.value)}
              maxLength={500}
              placeholder="Contexto: qué incluye, supuestos, etc."
              data-testid="define-budget-notes"
            />
          </div>
          {err && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2" data-testid="define-budget-error">{err}</div>}
          <div className="flex gap-2 pt-2">
            <button type="button" onClick={onClose} className="dv-btn dv-btn-ghost">Cancelar</button>
            <button type="submit" disabled={busy} className="dv-btn dv-btn-primary flex-1" data-testid="define-budget-submit">
              {busy ? "Guardando…" : "Guardar presupuesto"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
